// Demo 3: Winery Tasting + Direct Wine Order
// Fictional brand: "Vinaria Vardarska" — family winery in Valandovo region
// Pitch: tasting bookings + direct-to-consumer wine sales (no middleman)

import { useState } from 'react'
import DemoLayout from './shared/DemoLayout'
import { useT } from './shared/i18n-ctx'
import type { Dict } from './shared/i18n-ctx'

const dict: Dict = {
  hero_tag: { en: 'Wine tasting · Direct order · No middleman', mk: 'Дегустација · Директна нарачка · Без посредник', gr: 'Γευσιγνωσία · Απευθείας παραγγελία · Χωρίς ενδιάμεσο' },
  hero_title: { en: 'Demo · Winery', mk: 'Демо · Винарија', gr: 'Demo · Οινοποιείο' },
  hero_sub: {
    en: 'Template for any winery. Tasting bookings, direct-to-customer wine shop, wine-club subscriptions. The winemaker replaces logo + bottle photos + grapes.',
    mk: 'Шаблон за било која винарија. Резервации за дегустација, директна продажба, клуб на вино. Винарот ги менува логото, фотографиите и сортите.',
    gr: 'Πρότυπο για οινοποιείο. Κρατήσεις γευσιγνωσίας, απευθείας πώληση κρασιού, συνδρομές. Ο οινοποιός αλλάζει logo + κρασιά.',
  },
  book: { en: 'Book a tasting', mk: 'Резервирај дегустација', gr: 'Κλείσε γευσιγνωσία' },
  shop: { en: 'Shop wines', mk: 'Купи вина', gr: 'Αγόρασε κρασιά' },
  tastings_title: { en: 'Tasting experiences', mk: 'Дегустациски пакети', gr: 'Πακέτα γευσιγνωσίας' },
  tasting_classic_name: { en: 'Classic Tasting', mk: 'Класична дегустација', gr: 'Κλασική γευσιγνωσία' },
  tasting_classic_desc: { en: '90 min · 5 wines · cheese board · cellar tour', mk: '90 мин · 5 вина · сирење · посета на подрум', gr: '90 λεπτά · 5 κρασιά · τυροκομικά · επίσκεψη κελαριού' },
  tasting_premium_name: { en: 'Premium Pairing', mk: 'Премиум комбинација', gr: 'Premium συνδυασμός' },
  tasting_premium_desc: { en: '2 hrs · 7 wines · 4-course meal · winemaker hosted', mk: '2 часа · 7 вина · 4 текови · со винарот', gr: '2 ώρες · 7 κρασιά · 4 πιάτα · με τον οινοποιό' },
  tasting_harvest_name: { en: 'Harvest Day', mk: 'Ден на бербата', gr: 'Ημέρα συγκομιδής' },
  tasting_harvest_desc: { en: 'Sep–Oct only · pick grapes · press your own · dinner', mk: 'Сеп–Окт само · берба грозје · пресувај свое · вечера', gr: 'Μόνο Σεπ–Οκτ · συγκομιδή · δική σου πίεση · δείπνο' },
  per_person: { en: 'per person', mk: 'по лице', gr: 'ανά άτομο' },
  reserve: { en: 'Reserve', mk: 'Резервирај', gr: 'Κράτηση' },
  select_date: { en: 'Select date', mk: 'Избери датум', gr: 'Επιλογή ημερομηνίας' },
  guests: { en: 'Guests', mk: 'Гости', gr: 'Άτομα' },
  name: { en: 'Your name', mk: 'Твое име', gr: 'Όνομα' },
  email: { en: 'Email', mk: 'Е-пошта', gr: 'Email' },
  confirm: { en: 'Confirm booking', mk: 'Потврди', gr: 'Επιβεβαίωση' },
  booked: { en: '✓ Tasting booked! We sent details by email.', mk: '✓ Дегустацијата е резервирана! Пративме детали по е-пошта.', gr: '✓ Η γευσιγνωσία κλείστηκε! Σας στείλαμε email.' },
  shop_title: { en: 'Order wine direct to your door', mk: 'Нарачај вино директно дома', gr: 'Παραγγελία κρασιού σπίτι σου' },
  add_cart: { en: 'Add', mk: 'Додај', gr: 'Προσθήκη' },
  cart: { en: 'Your cart', mk: 'Кошничка', gr: 'Καλάθι' },
  empty_cart: { en: 'Cart is empty — add a bottle!', mk: 'Кошничката е празна — додај шише!', gr: 'Το καλάθι είναι άδειο!' },
  checkout: { en: 'Checkout', mk: 'На каса', gr: 'Ολοκλήρωση' },
  ordered: { en: '✓ Order placed. Your wine ships in 2 days.', mk: '✓ Нарачката е испратена. Виното пристигнува за 2 дена.', gr: '✓ Η παραγγελία ολοκληρώθηκε. Αποστολή σε 2 ημέρες.' },
  vintage: { en: 'Vintage', mk: 'Година', gr: 'Σοδειά' },
  bottle: { en: '750ml', mk: '750мл', gr: '750ml' },
}

type Wine = { id: string; name: string; grape: string; vintage: number; price: number; color: 'red' | 'white' | 'rose' }
const WINES: Wine[] = [
  { id: 'vranec-24', name: 'Vranec Reserve', grape: 'Vranec', vintage: 2024, price: 12, color: 'red' },
  { id: 'kratosija-25', name: 'Kratošija', grape: 'Kratošija', vintage: 2025, price: 10, color: 'red' },
  { id: 'temjanika-25', name: 'Temjanika', grape: 'Temjanika', vintage: 2025, price: 9, color: 'white' },
  { id: 'rosé-25', name: 'Rosé Vardarska', grape: 'Vranec', vintage: 2025, price: 8, color: 'rose' },
  { id: 'chardonnay-24', name: 'Chardonnay Barrique', grape: 'Chardonnay', vintage: 2024, price: 14, color: 'white' },
  { id: 'merlot-23', name: 'Merlot Selekcija', grape: 'Merlot', vintage: 2023, price: 13, color: 'red' },
]

const TASTINGS = [
  { key: 'classic', price: 25 },
  { key: 'premium', price: 55 },
  { key: 'harvest', price: 85 },
] as const

const colorStyles: Record<Wine['color'], string> = {
  red: 'bg-red-500/10 text-red-400 border-red-500/20',
  white: 'bg-yellow-500/10 text-yellow-300 border-yellow-500/20',
  rose: 'bg-pink-500/10 text-pink-300 border-pink-500/20',
}

export default function WineryDemo() {
  return (
    <DemoLayout
      dict={dict}
      accent="from-rose-600 to-purple-700"
      chatPersona={{
        name: 'Winery',
        kind: 'booking',
        items: [
          { name: 'Wine tasting (5 wines)', price: 18 },
          { name: 'Vineyard tour + tasting', price: 30 },
          { name: 'Cheese & charcuterie board', price: 14 },
          { name: 'Private group booking', price: 120 },
          { name: 'Bottle of house red', price: 12 },
        ],
      }}
    >
      <Body />
    </DemoLayout>
  )
}

function Body() {
  const { t } = useT()
  const [cart, setCart] = useState<Record<string, number>>({})
  const [ordered, setOrdered] = useState(false)
  const [tastingBooked, setTastingBooked] = useState<string | null>(null)
  const [tForm, setTForm] = useState({ date: '', guests: 2, name: '', email: '', key: '' })

  function addToCart(id: string) {
    setOrdered(false)
    setCart((c) => ({ ...c, [id]: (c[id] ?? 0) + 1 }))
  }

  const cartItems = Object.entries(cart).filter(([, n]) => n > 0)
  const cartTotal = cartItems.reduce((sum, [id, n]) => sum + (WINES.find((w) => w.id === id)?.price ?? 0) * n, 0)

  return (
    <div className="bg-gradient-to-b from-ink-950 via-ink-950 to-ink-900">
      <section className="relative">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?auto=format&fit=crop&w=1600&q=70)',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <span className="chip bg-rose-500/10 text-rose-400 border border-rose-500/20 mb-5">
            🍷 {t('hero_tag')}
          </span>
          <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-white max-w-3xl">{t('hero_title')}</h1>
          <p className="mt-5 text-lg text-ink-100 max-w-2xl">{t('hero_sub')}</p>
          <div className="mt-6 flex gap-3 flex-wrap">
            <a href="#tastings" className="btn-primary">{t('book')}</a>
            <a href="#shop" className="btn-secondary">{t('shop')}</a>
          </div>
        </div>
      </section>

      {/* Tastings */}
      <section id="tastings" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-3xl font-bold text-white mb-8">{t('tastings_title')}</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {TASTINGS.map(({ key, price }) => (
            <div key={key} className="card">
              <h3 className="font-semibold text-white text-lg">{t(`tasting_${key}_name`)}</h3>
              <p className="text-ink-500 text-sm mt-2">{t(`tasting_${key}_desc`)}</p>
              <div className="mt-4 text-3xl font-bold text-white">
                €{price}<span className="text-sm text-ink-500 font-normal"> {t('per_person')}</span>
              </div>
              <button onClick={() => setTForm({ ...tForm, key })} className="btn-primary w-full mt-4">{t('reserve')}</button>
            </div>
          ))}
        </div>

        {tForm.key && !tastingBooked && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              setTastingBooked(tForm.key)
            }}
            className="card mt-8 max-w-xl grid gap-4 sm:grid-cols-2"
          >
            <label><span className="label">{t('select_date')}</span><input required type="date" className="input" value={tForm.date} onChange={(e) => setTForm({ ...tForm, date: e.target.value })} /></label>
            <label><span className="label">{t('guests')}</span><input required type="number" min={1} max={12} className="input" value={tForm.guests} onChange={(e) => setTForm({ ...tForm, guests: +e.target.value || 1 })} /></label>
            <label><span className="label">{t('name')}</span><input required className="input" value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} /></label>
            <label><span className="label">{t('email')}</span><input required type="email" className="input" value={tForm.email} onChange={(e) => setTForm({ ...tForm, email: e.target.value })} /></label>
            <button type="submit" className="btn-primary sm:col-span-2">{t('confirm')}</button>
          </form>
        )}

        {tastingBooked && (
          <div className="mt-8 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 p-4 text-center font-medium max-w-xl">
            {t('booked')}
          </div>
        )}
      </section>

      {/* Shop */}
      <section id="shop" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pb-24">
        <h2 className="text-3xl font-bold text-white mb-8">{t('shop_title')}</h2>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {WINES.map((w) => (
            <div key={w.id} className="card">
              <div className="flex items-start justify-between gap-2 mb-3">
                <span className={`chip border ${colorStyles[w.color]}`}>{w.color}</span>
                <span className="chip bg-ink-800 text-ink-100 border border-ink-800 font-mono text-[11px]">{t('vintage')} {w.vintage}</span>
              </div>
              <h3 className="font-semibold text-white text-lg">{w.name}</h3>
              <p className="text-ink-500 text-sm">{w.grape} · {t('bottle')}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-2xl font-bold text-white">€{w.price}</span>
                <button onClick={() => addToCart(w.id)} className="btn-secondary text-sm py-2">{t('add_cart')}</button>
              </div>
            </div>
          ))}
        </div>

        {/* Cart */}
        <div className="mt-10 card max-w-xl">
          <h3 className="font-semibold text-white text-lg mb-3">{t('cart')}</h3>
          {cartItems.length === 0 ? (
            <p className="text-ink-500 text-sm">{t('empty_cart')}</p>
          ) : (
            <>
              <ul className="divide-y divide-ink-800">
                {cartItems.map(([id, n]) => {
                  const w = WINES.find((x) => x.id === id)!
                  return (
                    <li key={id} className="flex items-center justify-between py-2 text-sm">
                      <span className="text-ink-100">{w.name} × {n}</span>
                      <span className="text-ink-100 font-mono">€{(w.price * n).toFixed(2)}</span>
                    </li>
                  )
                })}
              </ul>
              <div className="mt-3 flex items-center justify-between text-white font-semibold">
                <span>Total</span>
                <span>€{cartTotal.toFixed(2)}</span>
              </div>
              <button
                onClick={() => {
                  setOrdered(true)
                  setCart({})
                }}
                className="btn-primary w-full mt-4"
              >
                {t('checkout')}
              </button>
            </>
          )}
          {ordered && (
            <div className="mt-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 p-3 text-center text-sm font-medium">
              {t('ordered')}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
